import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, TextField } from '@pulse/ui';
import type { ComponentProps } from 'react';

// Single source of truth for the app's search-box icon adornment — used directly by
// `SearchField` below, and imported as-is by any search field that can't go through the full
// `SearchField` wrapper (e.g. `EntityPicker`'s internal search fields, embedded in an
// Autocomplete's `renderInput` and needing its own `params`/`ref` wiring), so every search box
// in the app renders the exact same icon/size/color even where the wrapper component itself
// doesn't fit.
export function searchAdornment() {
  return <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>;
}

export interface SearchFieldProps extends Omit<ComponentProps<typeof TextField>, 'value' | 'onChange' | 'InputProps'> {
  value: string;
  onChange: (value: string) => void;
  /** Hides the search icon once the field has text, so it reads as part of the placeholder
   *  rather than a persistent adornment. Opt-in — every plain search box in the app keeps the
   *  icon visible at all times except the Project Board issue search. */
  hideIconWhileTyping?: boolean;
}

// Shared plain search box (placeholder text, no floating label) — every page-level "Search
// projects…"/"Search staffing…"-style box in the app should use this instead of hand-rolling
// its own `InputProps={{ startAdornment: <InputAdornment><SearchIcon /></InputAdornment> }}`.
// Not a fit for a field with a real floating label (a page that deliberately uses LabelWithIcon
// instead of a placeholder), or one embedded in an Autocomplete's `renderInput` (see
// `searchAdornment` above for that case) — both need their own markup, but should still render
// via `searchAdornment()` for the icon itself.
export function SearchField({ value, onChange, hideIconWhileTyping, size = 'small', ...rest }: SearchFieldProps) {
  const showIcon = !hideIconWhileTyping || value === '';
  return (
    <TextField
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size={size}
      InputProps={{ startAdornment: showIcon ? searchAdornment() : undefined }}
      {...rest}
    />
  );
}
